import { Action, Reducer } from 'redux';
import { fork, call, takeEvery, put, all, select, delay } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import { ITableConfig } from '../components/common/table';
import Api from '../api';
import { IRootState } from './index';
import { actionCreators as layoutActionCreators } from '../reducers/Layout';
import { selectors as authSelectors } from '../reducers/Auth';
import PagedResultSet from '../models/common/PagedResultSet';
import { ODataOptions } from '../util/ODataBuilder';
import api from '../api';
import { replace } from 'connected-react-router';
import Personnel from 'src/models/personnel/Personnel';
import TSDILandman from 'src/models/personnel/TSDILandman';

// Store
export interface IPersonnelState {
    isPersonnelLoading: boolean;
    createPersonnelLoading: boolean;
    personnel: PagedResultSet<TSDILandman> | undefined;
    personnelId: number | undefined;
    personnelPageTableConfig: ITableConfig;
    selectedPersonnel: TSDILandman;
}

const initialState: IPersonnelState = {
    isPersonnelLoading: false,
    createPersonnelLoading: false,
    personnel: undefined,
    personnelId: undefined,
    personnelPageTableConfig: {
        pageNumber: 1,
        rowCount: 15,
        idField: 'id',
        columns: [
            { name: 'Last Name', mapping: 'lastName' },
            { name: 'First Name', mapping: 'firstName' },
            { name: 'Corporation Name', mapping: 'corporationName' },
            { name: 'Federal Tax Id', mapping: 'federalTaxId' },
            { name: 'SSN', mapping: 'ssn' },
            { name: 'Email', mapping: 'username' },
        ],
    },
    selectedPersonnel: undefined,
};

// Action Types
export enum actionTypes {
    GET_ALL_PERSONNEL = '[personnel] GET_ALL_PERSONNEL',
    GET_ALL_PERSONNEL_SUCCESS = '[personnel] GET_ALL_PERSONNEL_SUCCESS',
    GET_PERSONNEL = '[personnel] GET_PERSONNEL',
    CREATE_PERSONNEL = '[personnel] CREATE_PERSONNEL',
    CREATE_PERSONNEL_SUCCESS = '[personnel] CREATE_PERSONNEL_SUCCESS',
    GET_PERSONNEL_SUCCESS = '[personnel] GET_PERSONNEL_SUCCESS',
    SET_SELECTED_PERSONNEL = '[personnel] SET_SELECTED_PERSONNEL',
    SET_SELECTED_PERSONNEL_SUCCESS = '[personnel] SET_SELECTED_PERSONNEL_SUCCESS',
    INCREMENT_PERSONNEL_PAGE = '[personnel] INCREMENT_PERSONNEL_PAGE',
    DECREMENT_PERSONNEL_PAGE = '[personnel] DECREMENT_PERSONNEL_PAGE',
    INITIALIZE_PERSONNEL_PAGE = '[personnel] INITIALIZE_PERSONNEL_PAGE'
}

// Action Creator
export const actionCreators = {
    incrementPersonnelPage: () => {
        return { type: actionTypes.INCREMENT_PERSONNEL_PAGE };
    },
    decrementPersonnelPage: () => {
        return { type: actionTypes.DECREMENT_PERSONNEL_PAGE };
    },
    initializePersonnelPage: () => {
        return { type: actionTypes.INITIALIZE_PERSONNEL_PAGE };
    },
    getAllPersonnel: (options: ODataOptions, resetPage?: boolean) => {
        return { type: actionTypes.GET_ALL_PERSONNEL, payload: { options, resetPage } };
    },
    getAllPersonnelSuccess: (personnel: PagedResultSet<Personnel>, resetPage?: boolean) => {
        return { type: actionTypes.GET_ALL_PERSONNEL_SUCCESS, payload: {personnel, resetPage} };
    },
    getPersonnel: (id: number) => {
        return { type: actionTypes.GET_PERSONNEL, payload: id };
    },
    createPersonnel: (personnel: Personnel) => {
        return { type: actionTypes.CREATE_PERSONNEL, payload: personnel };
    },
    createPersonnelSuccess: () => {
        return { type: actionTypes.CREATE_PERSONNEL_SUCCESS };
    },
    getPersonnelSuccess: (personnel: Personnel) => {
        return { type: actionTypes.GET_PERSONNEL_SUCCESS, payload: personnel };
    },
    setPersonnel: (personnel: Personnel) => {
        return { type: actionTypes.SET_SELECTED_PERSONNEL_SUCCESS, payload: personnel};
    },
};

// Reducer
const reducer: Reducer<IPersonnelState> = (state: IPersonnelState = initialState, action: Action): IPersonnelState => {
    switch (action.type) {
        case actionTypes.GET_PERSONNEL:
            return {
                ...state,
                isPersonnelLoading: true,
            };
        case actionTypes.GET_ALL_PERSONNEL:
            return {
                ...state,
                personnel: initialState.personnel,
                isPersonnelLoading: true,
            };
        case actionTypes.GET_PERSONNEL_SUCCESS:
            const getPersonnelPayload = (action as { type: string; payload: TSDILandman }).payload;
            const personnelResultSet = new PagedResultSet<TSDILandman>();
            personnelResultSet.items.push(getPersonnelPayload);
            return {
                ...state,
                personnel: personnelResultSet,
                isPersonnelLoading: false,
            };
        case actionTypes.GET_ALL_PERSONNEL_SUCCESS: {
            const payload = (action as { type: string; payload: { personnel: PagedResultSet<TSDILandman>; resetPage: boolean } }).payload;

            if (payload.resetPage) {
                return {
                    ...state,
                    personnel: payload.personnel,
                    isPersonnelLoading: false
                };
            } else if (state.personnel === undefined || state.personnel.pageNumber < payload.personnel.pageNumber) {
                const newPersonnel = { ...state.personnel };
                newPersonnel.pageNumber = payload.personnel.pageNumber;
                newPersonnel.pageSize = payload.personnel.pageSize;
                newPersonnel.totalRecords = payload.personnel.totalRecords;

                if (!newPersonnel.items) {
                    newPersonnel.items = [];
                }
                newPersonnel.items = newPersonnel.items.concat(payload.personnel.items);

                return {
                    ...state,
                    personnel: newPersonnel,
                    isPersonnelLoading: false,
                };
            }

            return state;
        }
        case actionTypes.CREATE_PERSONNEL: {
            return {
                ...state,
                createPersonnelLoading: true,
            };
        }
        case actionTypes.CREATE_PERSONNEL_SUCCESS: {
            return {
                ...state,
                createPersonnelLoading: false,
            };
        }
        case actionTypes.SET_SELECTED_PERSONNEL_SUCCESS: {
            const payload = (action as { type: string; payload: TSDILandman }).payload;
            return {
                ...state,
                selectedPersonnel: payload
            };
        }
        case actionTypes.INCREMENT_PERSONNEL_PAGE:
            return {
                ...state,
                personnelPageTableConfig: {
                    ...state.personnelPageTableConfig,
                    pageNumber: state.personnelPageTableConfig.pageNumber + 1,
                },
            };
        case actionTypes.DECREMENT_PERSONNEL_PAGE:
            return {
                ...state,
                personnelPageTableConfig: {
                    ...state.personnelPageTableConfig,
                    pageNumber:
                        state.personnelPageTableConfig.pageNumber > 1
                            ? state.personnelPageTableConfig.pageNumber - 1
                            : state.personnelPageTableConfig.pageNumber,
                },
            };
        case actionTypes.INITIALIZE_PERSONNEL_PAGE:
            return {
                ...state,
                personnelPageTableConfig: {
                    ...state.personnelPageTableConfig,
                    pageNumber: 1,
                },
            };
        default:
            return state;
    }
};

export default reducer;

const allPersonnelSelector = (state: IRootState): PagedResultSet<TSDILandman> | undefined => state.personnel.personnel;
const personnelSelector = (lmNo: number) =>
    createSelector(allPersonnelSelector, (p) =>
        p ? p.items.filter((pEntity: TSDILandman) => pEntity.lmNo === Number.parseInt(lmNo + '', 10))[0] : undefined
    );

export const selectors = {
    isPersonnelLoading: (state: IRootState): boolean => state.personnel.isPersonnelLoading || state.layout.initialLoading,
    isContactInfoLoading: (state: IRootState): boolean => state.personnel.isPersonnelLoading || state.layout.initialLoading,
    allPersonnel: allPersonnelSelector,
    personnel: personnelSelector,
    personnelPageTableConfig: (state: IRootState): ITableConfig => state.personnel.personnelPageTableConfig,
    selectedPersonnel: (state: IRootState): TSDILandman => state.personnel.selectedPersonnel
};

// Side-Effects
const sagas = {
    getAllPersonnel: function* (action: { type: string; payload: { options: ODataOptions; resetPage: boolean } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const tableConfig = yield select(selectors.personnelPageTableConfig);
        const options = ODataOptions.from(tableConfig);

        if (action.payload) {
            options.merge(action.payload.options);
        }
        
        const [result] = yield all([call(Api.Personnel.getAll, options, jwt), delay(200)]);
        yield put(actionCreators.getAllPersonnelSuccess(result, action.payload && action.payload.resetPage));
    },
    getPersonnel: function* (action: { type: string; payload: number }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const personnelId = action.payload;
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Personnel.get, personnelId, jwt), delay(200)]);
        yield put(actionCreators.getPersonnelSuccess(result));
    },
    createPersonnel: function* (action: { type: string; payload: Personnel }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const result = yield call(api.Personnel.create, action.payload, jwt);
        yield put(actionCreators.createPersonnelSuccess());
        yield put(replace(`/personnel/${result.id}`));
    },
    setPersonnel: function* (action: { type: string; payload: Personnel}): unknown {
        yield put(actionCreators.setPersonnel(action.payload));
    },
    
};

const watches = {
    incrementClick: function* () {
        yield takeEvery(actionTypes.INCREMENT_PERSONNEL_PAGE, sagas.getAllPersonnel);
    },
    decrementClick: function* () {
        yield takeEvery(actionTypes.DECREMENT_PERSONNEL_PAGE, sagas.getAllPersonnel);
    },
    getAllPersonnel: function* () {
        yield takeEvery(actionTypes.GET_ALL_PERSONNEL, sagas.getAllPersonnel);
    },
    getPersonnel: function* () {
        yield takeEvery(actionTypes.GET_PERSONNEL, sagas.getPersonnel);
    },
    createPersonnel: function* () {
        yield takeEvery(actionTypes.CREATE_PERSONNEL, sagas.createPersonnel);
    },
    setPersonnel: function* () {
        yield takeEvery(actionTypes.SET_SELECTED_PERSONNEL, sagas.setPersonnel);
    }
};

export function* saga() {
    yield fork(watches.incrementClick);
    yield fork(watches.decrementClick);
    yield fork(watches.getAllPersonnel);
    yield fork(watches.getPersonnel);
    yield fork(watches.createPersonnel);
    yield fork(watches.setPersonnel);
}



// WEBPACK FOOTER //
// ./src/reducers/Personnel.ts